import { Box, Button, InputLabel, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { AiOutlinePlus } from "react-icons/ai";
import { RiPencilFill } from "react-icons/ri";
import ReactModal from "../../../../Components/Modal/Modal";
import Input from "../../../../Components/Input/Input";
import { useStyle as useDepartmentStyles } from "../HRMSetup/Departments/department-styles";
import { BaseURL } from "../../../../Utils/interceptors";
import { getToken } from "../../../../Utils/helper";
import axios from "axios";
import { toast } from "react-toastify";
import {
  formatAmountInputDisplay,
  parseAmountNumber,
  preventInvalidAmountKeys,
} from "../../../../Utils/helper";
import { toIssueDateInputValue } from "./loanRequestUtils";
import "../TimeSheet/TimeSheet.css";
import "../StaffManagement/AllEmployees/Employees.css";

const amountInputProps = {
  inputMode: "decimal",
  onKeyDown: preventInvalidAmountKeys,
};

const LoanPaymentHrActions = ({ loan, isHr, onUpdated }) => {
  const deptClasses = useDepartmentStyles();
  const [showEditAmountsModal, setShowEditAmountsModal] = useState(false);
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [month, setMonth] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editServiceCharge, setEditServiceCharge] = useState("");
  const [editIssueDate, setEditIssueDate] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [amountsLoading, setAmountsLoading] = useState(false);

  useEffect(() => {
    setEditAmount(formatAmountInputDisplay(String(loan?.amount ?? "")));
    setEditServiceCharge(
      formatAmountInputDisplay(String(loan?.service_charge ?? 0)),
    );
    setEditIssueDate(toIssueDateInputValue(loan?.issue_date));
  }, [loan?.amount, loan?.service_charge, loan?.issue_date]);

  if (!loan || !isHr || loan.hr_status !== "approved") {
    return null;
  }

  const apiOptions = () => ({
    headers: {
      "Content-Type": "application/json",
      ...(getToken() && { Authorization: `Bearer ${getToken()}` }),
    },
  });

  const openEditAmountsModal = () => {
    setEditAmount(formatAmountInputDisplay(String(loan?.amount ?? "")));
    setEditServiceCharge(
      formatAmountInputDisplay(String(loan?.service_charge ?? 0)),
    );
    setEditIssueDate(toIssueDateInputValue(loan?.issue_date));
    setShowEditAmountsModal(true);
  };

  const openRecordPaymentModal = () => {
    setMonth("");
    setPaymentAmount("");
    setShowRecordPaymentModal(true);
  };

  const handleSaveAmounts = async () => {
    if (!editIssueDate) {
      toast.error("Please select issue date");
      return;
    }
    try {
      setAmountsLoading(true);
      const res = await axios.put(
        `${BaseURL}/loan-request/${loan._id}`,
        {
          amount: parseAmountNumber(editAmount),
          service_charge: parseAmountNumber(editServiceCharge) || 0,
          issue_date: editIssueDate,
        },
        apiOptions(),
      );
      toast.success(res?.data?.message);
      setShowEditAmountsModal(false);
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update amounts");
    } finally {
      setAmountsLoading(false);
    }
  };

  const handleAddPayment = async () => {
    if (!month || !paymentAmount) {
      toast.error("Please select month and enter payment amount");
      return;
    }
    try {
      setPaymentLoading(true);
      const res = await axios.post(
        `${BaseURL}/loan-request/${loan._id}/payment`,
        { month, amount: parseAmountNumber(paymentAmount) },
        apiOptions(),
      );
      toast.success(res?.data?.message);
      setShowRecordPaymentModal(false);
      setMonth("");
      setPaymentAmount("");
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to record payment");
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <>
      <ReactModal
        open={showEditAmountsModal}
        setOpen={setShowEditAmountsModal}
        title="Edit Amounts"
      >
        <Box sx={{ my: 2 }}>
          <InputLabel className={deptClasses.department_label}>
            Loan Amount (Rs)
          </InputLabel>
          <TextField
            fullWidth
            className={deptClasses.inputContainer}
            type="text"
            value={editAmount}
            inputProps={amountInputProps}
            onChange={(e) =>
              setEditAmount(formatAmountInputDisplay(e.target.value))
            }
            sx={{ mt: 1 }}
          />
        </Box>
        <Box sx={{ my: 2 }}>
          <InputLabel className={deptClasses.department_label}>
            Service Charge (Rs)
          </InputLabel>
          <TextField
            fullWidth
            className={deptClasses.inputContainer}
            type="text"
            value={editServiceCharge}
            inputProps={amountInputProps}
            onChange={(e) =>
              setEditServiceCharge(formatAmountInputDisplay(e.target.value))
            }
            sx={{ mt: 1 }}
          />
        </Box>
        <Box sx={{ my: 2 }}>
          <Input>
            <Form.Label>Issue Date</Form.Label>
            <Form.Control
              type="date"
              value={editIssueDate}
              onChange={(e) => setEditIssueDate(e.target.value)}
            />
          </Input>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Box sx={{ display: "flex", gap: "10px" }}>
            <Button
              variant="contained"
              className={deptClasses.cancelBtn}
              onClick={() => setShowEditAmountsModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className={deptClasses.createBtn}
              onClick={handleSaveAmounts}
              disabled={amountsLoading}
            >
              {amountsLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </Box>
      </ReactModal>

      <ReactModal
        open={showRecordPaymentModal}
        setOpen={setShowRecordPaymentModal}
        title="Record Payment"
      >
        <Box sx={{ my: 2 }}>
          <Input>
            <Form.Label>Month/Year</Form.Label>
            <Form.Control
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </Input>
        </Box>
        <Box sx={{ my: 2 }}>
          <InputLabel className={deptClasses.department_label}>
            Amount (Rs)
          </InputLabel>
          <TextField
            fullWidth
            className={deptClasses.inputContainer}
            type="text"
            value={paymentAmount}
            inputProps={amountInputProps}
            onChange={(e) =>
              setPaymentAmount(formatAmountInputDisplay(e.target.value))
            }
            sx={{ mt: 1 }}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Box sx={{ display: "flex", gap: "10px" }}>
            <Button
              variant="contained"
              className={deptClasses.cancelBtn}
              onClick={() => setShowRecordPaymentModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className={deptClasses.createBtn}
              onClick={handleAddPayment}
              disabled={paymentLoading}
            >
              {paymentLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Submit"
              )}
            </Button>
          </Box>
        </Box>
      </ReactModal>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
          gap: 1,
          mx: 4,
          mt: 2,
          mb: 2,
        }}
      >
        <div
          className="search_btn_container"
          style={{ width: "auto", marginBottom: 0 }}
        >
          <button type="button" onClick={openEditAmountsModal}>
            <RiPencilFill />
            Edit Amounts
          </button>
        </div>
        <div
          className="search_btn_container"
          style={{ width: "auto", marginBottom: 0 }}
        >
          <button
            type="button"
            className="create__attendence__btn__color"
            onClick={openRecordPaymentModal}
          >
            <AiOutlinePlus style={{ marginRight: "4px" }} />
            Record Payment
          </button>
        </div>
      </Box>
    </>
  );
};

export default LoanPaymentHrActions;
